import React, { useState, useEffect } from "react";
import { Col, Container, Row, Button, Form, Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import SingleDetailHeader from "../../components/singleDetail/SingleDetailHeader";
import BreadCrumb from "../../components/breadcrumb/BreadCrumb";
import Notification, { showToast } from "../../../components/common/notification/Notification";

const AddLease = () => {
  const navigate = useNavigate();
  const authState = useSelector((state) => state.auth);
  const loggedInUser = authState?.user ?? authState;
  const token = loggedInUser?.refreshToken || authState?.refreshToken || loggedInUser?.token || authState?.token;
  const baseUrl = process.env.REACT_APP_BASE_URL;

  const [loading, setLoading] = useState(false);
  const [purposes, setPurposes] = useState([]);
  const [fetchingPurposes, setFetchingPurposes] = useState(true);

  const [formData, setFormData] = useState({
    title: "",
    purposeId: "",
    budget: "",
    city: "",
    state: "",
    description: "",
  });

  useEffect(() => {
    const fetchPurposes = async () => {
      try {
        const response = await axios.post(
          `${baseUrl}/lease-purpose/all`,
          { page_number: 1, page_size: 100, order_by: "DESC" },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const resData = response.data;
        const list =
          resData?.leasePurposes ||
          resData?.data ||
          resData?.content ||
          resData?.lease_purposes ||
          (Array.isArray(resData) ? resData : []);

        setPurposes(list);
      } catch (err) {
        console.error("Error fetching lease purposes:", err);
      } finally {
        setFetchingPurposes(false);
      }
    };

    fetchPurposes();
  }, [baseUrl, token]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      showToast("Please enter a title for the lease.", { type: "error" });
      return;
    }
    if (!formData.purposeId) {
      showToast("Please select a lease purpose.", { type: "error" });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${baseUrl}/lease/add`,
        {
          title: formData.title,
          description: formData.description,
          budget: Number(formData.budget) || 0,
          city: formData.city,
          state: formData.state,
          purpose: {
            id: Number(formData.purposeId),
          },
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200 || response.data?.statusType === "SUCCESS") {
        showToast("Lease added successfully!", { type: "success" });
        setTimeout(() => {
          navigate("/lease");
        }, 1200);
      } else {
        showToast(response.data?.text || "Failed to add Lease.", { type: "error" });
      }
    } catch (error) {
      console.error("Error adding lease:", error);
      showToast("Error adding Lease. Please try again.", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const breadcrumbItems = [
    { label: "", link: "/admin", icon: "fa-solid fa-house" },
    { label: "Lease", link: "/lease" },
    { label: "Add Lease", link: "/lease/add-lease", active: true },
  ];

  return (
    <>
      <Notification />
      <div className="content-body2">
        <Container>
          <BreadCrumb items={breadcrumbItems} />
          <Row>
            <Col lg={12} md={12} sm={12}>
              <SingleDetailHeader
                header="Add Lease"
                description="Create a new lease request record."
              />

              <Form onSubmit={handleSubmit} className="bg-white p-4 rounded shadow-sm border mt-3">
                <Row>
                  <Col md={6}>
                    <Form.Group controlId="formTitle" className="mb-3">
                      <Form.Label className="fw-semibold">Title *</Form.Label>
                      <Form.Control
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        placeholder="e.g. 3 BHK Apartment for Lease"
                        required
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group controlId="formPurpose" className="mb-3">
                      <Form.Label className="fw-semibold">Lease Purpose *</Form.Label>
                      <Form.Select
                        name="purposeId"
                        value={formData.purposeId}
                        onChange={handleChange}
                        required
                        disabled={fetchingPurposes}
                      >
                        <option value="">Select Lease Purpose</option>
                        {purposes.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <Form.Group controlId="formBudget" className="mb-3">
                      <Form.Label className="fw-semibold">Budget (₹)</Form.Label>
                      <Form.Control
                        type="number"
                        name="budget"
                        value={formData.budget}
                        onChange={handleChange}
                        placeholder="e.g. 50000"
                      />
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group controlId="formState" className="mb-3">
                      <Form.Label className="fw-semibold">State</Form.Label>
                      <Form.Control
                        type="text"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        placeholder="e.g. Maharashtra"
                      />
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group controlId="formCity" className="mb-3">
                      <Form.Label className="fw-semibold">City</Form.Label>
                      <Form.Control
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        placeholder="e.g. Mumbai"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group controlId="formDescription" className="mb-4">
                  <Form.Label className="fw-semibold">Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Enter lease description..."
                  />
                </Form.Group>

                <div className="d-flex justify-content-end gap-2">
                  <Button variant="secondary" onClick={() => navigate("/lease")} disabled={loading}>
                    Cancel
                  </Button>
                  <Button variant="primary" type="submit" disabled={loading}>
                    {loading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Saving...
                      </>
                    ) : (
                      "Submit"
                    )}
                  </Button>
                </div>
              </Form>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default AddLease;
